// Per-store label origin: which ship-from address each connected store's labels
// use and the sender name printed at the top of the label. A change saves at
// once (address) or when the name field loses focus.
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Search } from 'lucide-react'
import { Card } from '../../../components/ui/card'
import { DataTableShell } from '../../../components/ui/data-table-shell'
import { useT } from '../../../i18n'
import { shipFromLocationsI18n } from '../../../i18n/admin/ship-from-locations'
import {
  LOCATIONS_QUERY_KEY,
  STORE_ASSIGNMENTS_QUERY_KEY,
  clearStoreSlipLogo,
  fetchLocations,
  fetchStoreAssignments,
  formatAddress,
  saveStoreAssignment,
  uploadStoreSlipLogo,
  type ShipFromLocation,
  type StoreAssignment,
  type StoreShipFrom,
} from './ship-from.lib'

const DEFAULT_OPTION = ''

function useStoreAssignments() {
  const qc = useQueryClient()
  const stores = useQuery({ queryKey: STORE_ASSIGNMENTS_QUERY_KEY, queryFn: fetchStoreAssignments })
  const locations = useQuery({ queryKey: LOCATIONS_QUERY_KEY, queryFn: fetchLocations })
  const [savedId, setSavedId] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: ({ store, patch }: { store: StoreShipFrom; patch: Partial<StoreAssignment> }) =>
      saveStoreAssignment(store.store_integration_id, {
        ship_from_location_id: store.ship_from_location_id,
        ship_from_name: store.ship_from_name,
        ...patch,
      }),
    onSuccess: (row) => {
      qc.setQueryData<{ items: StoreShipFrom[]; total: number }>(STORE_ASSIGNMENTS_QUERY_KEY, (old) =>
        old
          ? {
              ...old,
              items: old.items.map((s) =>
                s.store_integration_id === row.store_integration_id ? row : s,
              ),
            }
          : old,
      )
      setSavedId(row.store_integration_id)
    },
  })
  return { stores, locations, save, savedId }
}

function SenderNameInput({
  store,
  onSave,
}: {
  store: StoreShipFrom
  onSave: (name: string | null) => void
}) {
  const t = useT(shipFromLocationsI18n).stores
  const [value, setValue] = useState(store.ship_from_name ?? '')
  return (
    <input
      value={value}
      aria-label={t.senderNameFor(store.shop_name)}
      placeholder={store.shop_name}
      maxLength={100}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        const next = value.trim() || null
        if (next !== (store.ship_from_name ?? null)) onSave(next)
      }}
      className="min-h-9 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2.5 text-sm text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
    />
  )
}

function AddressSelect({
  store,
  locations,
  onSave,
}: {
  store: StoreShipFrom
  locations: ShipFromLocation[]
  onSave: (locationId: string | null) => void
}) {
  const t = useT(shipFromLocationsI18n).stores
  const fallback = locations.find((l) => l.is_default)
  return (
    <select
      value={store.ship_from_location_id ?? DEFAULT_OPTION}
      aria-label={t.addressFor(store.shop_name)}
      onChange={(e) => onSave(e.target.value || null)}
      className="min-h-9 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 text-sm text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
    >
      <option value={DEFAULT_OPTION}>
        {fallback ? t.defaultOption(formatAddress(fallback.address)) : t.noAddress}
      </option>
      {locations.map((l) => (
        <option key={l.id} value={l.id}>
          {formatAddress(l.address)}
        </option>
      ))}
    </select>
  )
}

function SlipLogoCell({ store }: { store: StoreShipFrom }) {
  const t = useT(shipFromLocationsI18n).stores
  const qc = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(false)
  const patchStore = (has: boolean) =>
    qc.setQueryData<{ items: StoreShipFrom[]; total: number }>(STORE_ASSIGNMENTS_QUERY_KEY, (old) =>
      old
        ? {
            ...old,
            items: old.items.map((s) =>
              s.store_integration_id === store.store_integration_id
                ? { ...s, has_slip_logo: has }
                : s,
            ),
          }
        : old,
    )
  const run = async (fn: () => Promise<{ has_slip_logo: boolean }>) => {
    setBusy(true)
    setError(false)
    try {
      const res = await fn()
      patchStore(res.has_slip_logo)
    } catch {
      setError(true)
    } finally {
      setBusy(false)
    }
  }
  const inputId = `slip-logo-${store.store_integration_id}`
  return (
    <div className="flex items-center gap-2">
      <input
        id={inputId}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) void run(() => uploadStoreSlipLogo(store.store_integration_id, file))
          e.target.value = ''
        }}
      />
      <label
        htmlFor={inputId}
        className={`cursor-pointer rounded-md border border-[var(--color-border)] px-2 py-1 text-xs ${busy ? 'opacity-50' : 'hover:bg-[var(--color-surface-muted)]'}`}
      >
        {store.has_slip_logo ? t.replace : t.upload}
      </label>
      {store.has_slip_logo && (
        <button
          type="button"
          disabled={busy}
          onClick={() => void run(() => clearStoreSlipLogo(store.store_integration_id))}
          className="text-xs text-[var(--color-danger)] hover:underline disabled:opacity-50"
        >
          {t.remove}
        </button>
      )}
      {error && <span className="text-xs text-[var(--color-danger)]">{t.failed}</span>}
    </div>
  )
}

function SavedMark({ show }: { show: boolean }) {
  const t = useT(shipFromLocationsI18n).stores
  return (
    <span
      className={`inline-flex w-14 items-center gap-1 text-xs text-[var(--color-success)] transition-opacity ${show ? 'opacity-100' : 'opacity-0'}`}
      aria-live="polite"
    >
      {show && (
        <>
          <Check className="h-3.5 w-3.5" aria-hidden /> {t.saved}
        </>
      )}
    </span>
  )
}

export function StoreShipFromSection() {
  const t = useT(shipFromLocationsI18n).stores
  const { stores, locations, save, savedId } = useStoreAssignments()
  const [query, setQuery] = useState('')
  const addresses = locations.data?.items ?? []
  const allStores = stores.data?.items
  const items = useMemo(() => allStores ?? [], [allStores])
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? items.filter((s) => s.shop_name.toLowerCase().includes(q)) : items
  }, [items, query])

  const setLocation = (store: StoreShipFrom, id: string | null) =>
    save.mutate({ store, patch: { ship_from_location_id: id } })
  const setName = (store: StoreShipFrom, name: string | null) =>
    save.mutate({ store, patch: { ship_from_name: name } })

  if (stores.isLoading || locations.isLoading) {
    return <Card className="p-6 text-sm text-[var(--color-text-muted)]">{t.loading}</Card>
  }
  if (stores.isError || locations.isError) {
    return (
      <Card className="p-6 text-sm text-[var(--color-danger)]">
        {t.loadError}
      </Card>
    )
  }

  const search = (
    <label className="relative block max-w-xs">
      <Search
        className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-muted)]"
        aria-hidden
      />
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t.searchPlaceholder(items.length)}
        aria-label={t.searchLabel}
        className="min-h-9 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] pl-8 pr-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
      />
    </label>
  )
  const errorLine = save.isError && (
    <p className="text-xs text-[var(--color-danger)]">{t.lastNotSaved}</p>
  )

  return (
    <Card className="space-y-3 p-4">
      {search}
      {errorLine}
      <DataTableShell variant="embedded">
        <table className="min-w-[640px]">
          <thead>
            <tr>
              <th className="w-[26%]">{t.colStore}</th>
              <th className="w-[34%]">{t.colShipFrom}</th>
              <th>{t.colSender}</th>
              <th>{t.colSlipLogo}</th>
              <th className="w-14" />
            </tr>
          </thead>
          <tbody>
            {visible.map((store) => (
              <tr key={store.store_integration_id}>
                <td className="py-1.5">
                  <span className="font-medium text-[var(--color-text)]">{store.shop_name}</span>
                  <span className="block text-xs capitalize text-[var(--color-text-muted)]">
                    {store.provider} · {store.org_name}
                  </span>
                </td>
                <td className="py-1.5">
                  <AddressSelect store={store} locations={addresses} onSave={(id) => setLocation(store, id)} />
                </td>
                <td className="py-1.5">
                  <SenderNameInput store={store} onSave={(n) => setName(store, n)} />
                </td>
                <td className="py-1.5">
                  <SlipLogoCell store={store} />
                </td>
                <td>
                  <SavedMark show={savedId === store.store_integration_id} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DataTableShell>
    </Card>
  )
}
